import React, { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import {
  CreateInjectionSiteDto,
  InjectionSiteDto,
  Side,
} from '@services/dtos'
import { injectionSiteSchema } from '@utils/schemas'
import { useAppDispatch } from '@hooks/reduxHooks'
import { Grid } from '@mui/material'
import {
  createInjectionSite,
  updateInjectionSite,
} from '@state/reducers/injectionSiteReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import InputField from '../inputs/InputField'
import SelectField from '../inputs/SelectField'
import { SideLabels } from '@utils/constants'
import { FormCrudHeader } from './FormCrudHeader'

const SIDE_OPTIONS =
  (Object.entries(SideLabels) as Array<[Side, string]>)
    .map(([value, label]) => ({ label, value }))

type Props = {
  editingInjectionSite?: InjectionSiteDto | null
  onClose: () => void
}

const InjectionSiteForm: React.FC<Props> = ({
  editingInjectionSite,
  onClose,
}) => {
  const {
    handleSubmit,
    reset,
    control,
    watch
  } = useForm<CreateInjectionSiteDto>({
    defaultValues: editingInjectionSite
      ? {
          code: editingInjectionSite.code,
          bodyPart: editingInjectionSite.bodyPart,
          side: editingInjectionSite.side,
        }
      : { side: Side.NA },
    resolver: yupResolver(injectionSiteSchema),
  })

  const dispatch = useAppDispatch()

  const [isSubmitting, setIsSubmitting] = useState(false)

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  useEffect(() => {
    return () => {
      reset()
    }
  }, [dispatch])

  const onSubmitForm = async (data: CreateInjectionSiteDto) => {
    setIsSubmitting(true)

    if (editingInjectionSite) {
      dispatch(updateInjectionSite({ id: editingInjectionSite.id, dto: data }))
        .unwrap()
        .then(() => {
          cb('Le site d\'injection a été modifié avec succès')
          onClose()
        })
        .catch(() => cb('Une erreur est survenue, vérifiez que le code n\'est pas déjà utilisé'))
        .finally(() => setIsSubmitting(false))
    } else {
      dispatch(createInjectionSite(data))
        .unwrap()
        .then(() => {
          cb('Le site d\'injection a été créé avec succès')
          onClose()
        })
        .catch(() => cb('Une erreur est survenue, vérifiez que le code n\'est pas déjà utilisé'))
        .finally(() => setIsSubmitting(false))
    }
  }

  return (
    <div className="flex w-full h-full p-4">
      <form onSubmit={handleSubmit(onSubmitForm)} className="w-full">
        <FormCrudHeader
          isSubmitting={isSubmitting}
          onClose={onClose}
          editing={!!editingInjectionSite}
        />

        <div className="max-w-screen-sm mx-auto mt-4">
          <fieldset className="border border-gray-200 p-4 rounded-lg">
            <legend className="text-lg font-bold mb-4 border border-gray-200 p-2 rounded-lg">
              {'Site d\'injection'}
            </legend>

            <Grid container rowSpacing={2} columnSpacing={4} mt={1}>
              <Grid item xs={12} sm={6}>
                <InputField
                  control={control}
                  name={'code'}
                  label={'Code'}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <SelectField
                  key={'side'}
                  control={control}
                  name={'side'}
                  options={SIDE_OPTIONS}
                  value={watch('side')}
                  label={'Côté'}
                />
              </Grid>

              <Grid item xs={12}>
                <InputField
                  control={control}
                  name={'bodyPart'}
                  label={'Partie du corps'}
                />
              </Grid>
            </Grid>
          </fieldset>
        </div>
      </form>
    </div>
  )
}

export default InjectionSiteForm
